/**
 * IGRA-21 — `/vpn/overview` + `/vpn/activate-trial` API wrapper.
 *
 * Backend (`modules/vpn`, IGRA-12) is not built yet, so this reads the
 * IGRA-20 fixtures (`shared/api/mocks/vpnFixtures.ts`) whenever
 * `VITE_IGRA_USE_MOCKS=1`, following the pattern documented in that file.
 * When mocks are off, it calls the real endpoint — that request will 404
 * until IGRA-12 ships; this is an explicit, documented placeholder, not a
 * permanent mock (AGENTS.md: "Do not hide missing backend contracts with
 * permanent mock data").
 *
 * Dev-only mock state switch (browser console, no rebuild needed):
 *   localStorage.setItem('igra_vpn_mock_state', 'no_key' | 'expired' | 'pool_member' | 'pool_member_expired')
 *   localStorage.setItem('igra_vpn_mock_activate_fail', '1')  // independent — e.g. combine with 'no_key' to see the CTA fail
 *   location.reload()
 * Default (both unset) serves the IGRA-20 baseline fixture / a successful activation.
 */
import { httpClient } from '@/shared/api/httpClient'
import {
  isMocksEnabled,
  vpnOverviewFixture,
  vpnOverviewNoKeyFixture,
  vpnOverviewExpiredFixture,
  vpnOverviewPoolMemberFixture,
  vpnOverviewPoolMemberExpiredFixture,
  vpnActivateTrialFixture,
  VPN_UPSTREAM_UNAVAILABLE,
} from '@/shared/api/mocks/vpnFixtures'
import type { VpnActivateTrialResponse, VpnOverviewKey, VpnOverviewResponse } from '../types/vpn'

const MOCK_STATE_KEY = 'igra_vpn_mock_state'
const MOCK_ACTIVATE_FAIL_KEY = 'igra_vpn_mock_activate_fail'
type MockState = 'active' | 'no_key' | 'expired' | 'pool_member' | 'pool_member_expired'

function getMockState(): MockState {
  const raw = localStorage.getItem(MOCK_STATE_KEY)
  if (raw === 'no_key' || raw === 'expired') return raw
  // IGRA-110 — кабинет участника общей подписки и погасшая подписка владельца.
  if (raw === 'pool_member' || raw === 'pool_member_expired') return raw
  return 'active'
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Mimics the backend's `SpaceException`-style error body so
 * `classifyActivateTrialError` (vpnStore) exercises the same code path it
 * will use against the real backend. */
function upstreamUnavailableError(): Error {
  const error = new Error(VPN_UPSTREAM_UNAVAILABLE) as Error & { response: { data: unknown } }
  error.response = {
    data: {
      result: 'error',
      error_message: VPN_UPSTREAM_UNAVAILABLE,
    },
  }
  return error
}

/** IGRA-25 — the billing mock (`billingApi.ts`) records auto-renew toggles in
 * localStorage so a subsequent overview refetch reflects them, the same way
 * the real backend would after `POST /vpn/billing/(enable|cancel)-auto-renew`. */
function withMockAutoRenewOverride(overview: VpnOverviewResponse): VpnOverviewResponse {
  const override = localStorage.getItem('igra_vpn_mock_auto_renew')
  if (override !== '0' && override !== '1') return overview
  if (overview.access === null) return overview // IGRA-30: access нуллабелен
  return { ...overview, access: { ...overview.access, auto_renew: override === '1' } }
}

/** HK-IGRA.285 — the renew-period mock (`billingApi.setRenewPeriod`) records
 * the chosen term the same way: overview refetch reflects it like the real
 * backend's `pricing.renew_period` would. */
function withMockRenewPeriodOverride(overview: VpnOverviewResponse): VpnOverviewResponse {
  const period = localStorage.getItem('igra_vpn_mock_renew_period')
  if (period !== 'month' && period !== 'quarter' && period !== 'year') return overview
  return { ...overview, pricing: { ...overview.pricing, renew_period: period } }
}

export async function fetchOverview(): Promise<VpnOverviewResponse> {
  if (isMocksEnabled()) {
    await delay(300)
    const state = getMockState()
    if (state === 'no_key')
      return withMockRenewPeriodOverride(withMockAutoRenewOverride(vpnOverviewNoKeyFixture as VpnOverviewResponse))
    if (state === 'expired')
      return withMockRenewPeriodOverride(withMockAutoRenewOverride(vpnOverviewExpiredFixture as VpnOverviewResponse))
    if (state === 'pool_member')
      return withMockRenewPeriodOverride(
        withMockAutoRenewOverride(vpnOverviewPoolMemberFixture as VpnOverviewResponse),
      )
    if (state === 'pool_member_expired')
      return withMockRenewPeriodOverride(
        withMockAutoRenewOverride(vpnOverviewPoolMemberExpiredFixture as VpnOverviewResponse),
      )
    return withMockRenewPeriodOverride(withMockAutoRenewOverride(vpnOverviewFixture as VpnOverviewResponse))
  }

  const response = await httpClient.get<{ data: VpnOverviewResponse }>('/vpn/overview')
  return response.data
}

/**
 * IGRA-33 — `captchaToken` is mandatory: the backend (`VpnController::actionActivateTrial`
 * → `CaptchaService::verify()`) validates the captcha server-side and reads the
 * `captcha_token` body field.
 *
 * Дверь здесь БЕСПЛАТНАЯ (пробный доступ), поэтому капча осталась — в отличие
 * от `POST /vpn/public/checkout`, где шаг платный и капчу сняли 2026-09-10.
 */
export async function activateTrial(captchaToken: string): Promise<VpnActivateTrialResponse> {
  if (isMocksEnabled()) {
    await delay(500)
    if (localStorage.getItem(MOCK_ACTIVATE_FAIL_KEY) === '1') {
      throw upstreamUnavailableError()
    }
    // Idempotent per API-CONTRACT.md: subsequent overview fetches should now
    // report the active trial key — flip the dev mock switch so a page
    // reload (or the next fetchOverview() this action triggers) is consistent.
    localStorage.setItem(MOCK_STATE_KEY, 'active')
    return vpnActivateTrialFixture as VpnActivateTrialResponse
  }

  const response = await httpClient.post<{ data: VpnActivateTrialResponse }>('/vpn/activate-trial', {
    captcha_token: captchaToken,
  })
  return response.data
}

/**
 * HK-IGRA.664 — `POST /vpn/key/issue`: выпустить ключ оплатившему, у которого
 * ключа нет.
 *
 * Состояние «доступ живой, ключа нет» оставили после себя оплаты из кабинета,
 * сделанные до HK-IGRA.664 (бэкенд выпускал ключ только на входах, которые
 * предсоздавали его до счёта). Это и есть кнопка «Подключить устройство» на
 * «Главной»: другого пути починки у человека нет.
 *
 * Капчи здесь нет намеренно — в отличие от `activate-trial`. Дверь не выдаёт
 * ничего нового: без живого `vpn_access` бэкенд отказывает
 * (`VPN_ACCESS_INACTIVE`), то есть выпустить себе ключ может только тот, кто
 * уже заплатил.
 */
export async function issueKey(): Promise<VpnOverviewKey> {
  if (isMocksEnabled()) {
    await delay(500)
    localStorage.setItem(MOCK_STATE_KEY, 'active')
    return (vpnOverviewFixture as VpnOverviewResponse).key as VpnOverviewKey
  }

  const response = await httpClient.post<{ data: { key: VpnOverviewKey } }>('/vpn/key/issue', {})
  return response.data.key
}
